//
/* eslint-disable */
// Flow file with some potentially useful types, and also the ugly Platform relative imports

// vendor modules
import React, { Component } from "react"
import PropTypes from "prop-types"

import { generateFilterInfoObjs } from "shared/search/components/FilterableListView/helperFunctions"

import PlatformSpecificFilterableListView from "app/static/frontend/search-new/containers/FilterableListView/index"
import LoadingSpinner from "app/static/frontend/components/LoadingSpinner"

export class FilterableListView extends Component {
    static propTypes = {
        // passed from filterablelistviews.js
        filterSetComponent: PropTypes.func.isRequired,
        getRefreshablePermanentFilters: PropTypes.func,
        inlineComponent: PropTypes.func.isRequired,
        labelPlural: PropTypes.string,
        noResultsDetails: PropTypes.object,
        permanentFilters: PropTypes.object,
        profileInitialFilters: PropTypes.object,
        quorumDataType: PropTypes.number,
        resource: PropTypes.object,
        searchPlaceholderText: PropTypes.string,
        // passed via routes/Search/index.js
        uniqueFlvId: PropTypes.string.isRequired,
        disableDefaultFilters: PropTypes.bool,
        // passed via mobile actions
        initialFilters: PropTypes.object,
        // passed via profiles
        permanentFilterKey: PropTypes.string,
        orderBy: PropTypes.string,
        profileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        isProfile: PropTypes.bool,
        // passed from shared/search/containers/filterablelistview/index.js
        applyDefaultSearchFilters: PropTypes.func.isRequired,
        applyFiltersFromURL: PropTypes.func,
        applyInitialFilters: PropTypes.func.isRequired,
        applySearchifyInitialFilters: PropTypes.func.isRequired,
        configureFlvSliceWithFilters: PropTypes.func.isRequired,
        flvResource: PropTypes.object.isRequired,
        hasMoreDataToLoad: PropTypes.bool.isRequired,
        hasRegisteredFilters: PropTypes.bool.isRequired,
        isRegisteredFlv: PropTypes.bool.isRequired,
        modifySearchQueryURL: PropTypes.func,
        pushSearchifyLabel: PropTypes.func,
        registerFilterableListView: PropTypes.func.isRequired,
        registerFlvDataType: PropTypes.func.isRequired,
        registerPermanentFilters: PropTypes.func.isRequired,
        registerSerializationFunctions: PropTypes.func.isRequired,
        searchTerm: PropTypes.string,
        setSearchBarData: PropTypes.func,
        shouldDisableDefaultFilters: PropTypes.bool,
        storeProcessedDefaultFilters: PropTypes.func.isRequired,
        unregisterFilterableListView: PropTypes.func.isRequired,
        visitedSearch: PropTypes.func,
        // searchify
        isBulkActions: PropTypes.bool,
        isSearchify: PropTypes.bool.isRequired,
        currentPage: PropTypes.number.isRequired,
        currentCount: PropTypes.number,
        currentData: PropTypes.object,
        customFields: PropTypes.array.isRequired,
        actionCentersList: PropTypes.array.isRequired,
        alignmentTypes: PropTypes.object.isRequired,
        ledgerSettingsListIds: PropTypes.array.isRequired,
        hasMultipleBankAccounts: PropTypes.bool.isRequired,
    }

    state = { shouldRenderPresentation: false }

    // We want to set up the entire store before any of the components start rendering,
    // so we don't have to deal with processing filters after the fact. Hence,
    // this component registers the filterableListView and corresponding filters,
    // registers permanent filters, takes in URL filters (if any) and default filters (if any),
    // and then applies them.
    componentDidMount() {
        // registers FLV, filters, and default filters
        if (!this.props.isRegisteredFlv) {
            this.props.registerFilterableListView(this.props.uniqueFlvId)
        }

        if (this.props.isSearchify) {
            const labelPlural =
                this.props.labelPlural || DjangIO.app.models.QuorumDataType.by_value(this.props.quorumDataType).plural
            this.props.pushSearchifyLabel(this.props.uniqueFlvId, labelPlural)
        }

        if (!this.props.hasRegisteredFilters) {
            this.props.registerFlvDataType(
                this.props.uniqueFlvId,
                this.props.quorumDataType,
                this.props.flvResource.data_model,
            )

            // this is a crazy function. It will modify basically everything in the FLV
            // slice, and a few things in the search slice.
            const filterObjs = generateFilterInfoObjs({
                filterSet: this.props.filterSetComponent,
                customFields: this.props.customFields,
                profileId: this.props.profileId,
                isSearchify: this.props.isSearchify,
                actionCentersList: this.props.actionCentersList,
                alignmentTypes: this.props.alignmentTypes,
                ledgerSettingsListIds: this.props.ledgerSettingsListIds,
                hasMultipleBankAccounts: this.props.hasMultipleBankAccounts,
                quorumDataType: DjangIO.app.models.QuorumDataType.by_value(this.props.quorumDataType),
            })

            this.props.configureFlvSliceWithFilters(this.props.uniqueFlvId, filterObjs, this.props.quorumDataType)
            this.props.registerSerializationFunctions(filterObjs)
        }

        this.processDefaultFilters()
        this.processPermanentFilters()
        // apply URL filters or default filters or initial filters (searchify or mobile or something else)
        this.applyExternalFilters()

        // now that we've changed all filters we can, modify the URL to reflect the filter state!
        // we're doing this here instead of in the web component because we need
        // to catch the case where default filters are applied before the web component
        // is rendered, while also making sure that the URL doesn't change until
        // any existing URL filters are applied.
        if (this.props.modifySearchQueryURL && !this.props.isSearchify) {
            this.props.modifySearchQueryURL(this.props.uniqueFlvId)
        }

        // only after all these things are done should we start rendering
        // the presentational component!
        this.setState({ shouldRenderPresentation: true })

        if (this.props.visitedSearch && !this.props.isSearchify) {
            this.props.visitedSearch()
        }
    }

    componentDidUpdate(prevProps) {
        /**
         * If the FLV is already registered, and the URL changes, re-apply the URL filters
         * Note that the location prop is only provided in regular search, and will be missing in PowerSearch
         * Hence the check for isSearchify and the optional chaining on location.key for good measure.
         */
        if (this.props.isRegisteredFlv && !this.props.isSearchify && prevProps.location?.key !== this.props.location?.key) {
            this.props.applyFiltersFromURL(this.props.uniqueFlvId)
        }
    }

    applyExternalFilters() {
        /*
    * External filters come from a variety of headache-inducing sources:
    * -- Important (if applied, nothing else gets applied) --
    * 1. URL filters
    * 2. props.initialFilters, provided to the FLV (mobile equivalent of URL filters)
    * 3. searchifyInitialFilters, stored in the searchify slice of the store
    * -- Ancillary (only get applied if 1-3 don't exist) --
    * 4. A user's default search filters
    * 5. profileInitialFilters, passed through filterable list view specifications.
    *
    * Logical order:
    * 1. If you're in searchify and there are initial filters AND this is the first time
         searchify has loaded, apply them and return. If it's the second time (aka somebody
         went deeper into searchify and then clicked back), don't apply them.
    * 2. If there are filters from the URL, apply them and exit the function, regardless of whether or
         not this is the first time.
    * 3. If props.initialFilters and we're NOT in the web app, apply them and exit the function,
         regardless of whether or not this is the first time. [For use in mobile only. If you're
         in web, the URL should give you all you need.]
    * 4. If not 1, 2, or 3, two situations:
    *       a. NON-PROFILE: apply default filters.
    *       b. PROFILE: apply profileInitialFilters. (profiles have no default filters, but
    *          we want the ability to specify { current: True } on profiles for staffers
    *          regardless.)
    */
        let hasInitialFilters = false
        if (this.props.isSearchify && !this.props.isRegisteredFlv) {
            hasInitialFilters = this.props.applySearchifyInitialFilters(this.props.uniqueFlvId)
            if (
                this.props.initialFilters &&
                Object.keys(this.props.initialFilters).length &&
                "political_committee_is_active" in this.props.initialFilters
            ) {
                hasInitialFilters = true
                this.props.applyInitialFilters(this.props.uniqueFlvId, this.props.initialFilters)
            }
        } else if (!this.props.isSearchify) {
            if (this.props.initialFilters && Object.keys(this.props.initialFilters).length) {
                hasInitialFilters = true
                this.props.applyInitialFilters(this.props.uniqueFlvId, this.props.initialFilters)
                this.props.applyDefaultSearchFilters(this.props.uniqueFlvId)
            }
            if (this.props.applyFiltersFromURL) {
                hasInitialFilters = this.props.applyFiltersFromURL(this.props.uniqueFlvId)
            }
        }

        // in case there are URL filters, initial filters, or searchify initial filters,
        // or if we've already loaded this FLV before, there's no need to apply initial
        // or default search filters.
        if (hasInitialFilters || this.props.isRegisteredFlv) {
            return
        }

        // no default filters in profiles!
        if (!this.props.isProfile) {
            this.props.applyDefaultSearchFilters(this.props.uniqueFlvId)
        } else if (this.props.profileInitialFilters) {
            this.props.applyInitialFilters(this.props.uniqueFlvId, this.props.profileInitialFilters)
        }
    }

    processDefaultFilters() {
        // only process the default filters on the first pass-through; they should
        // otherwise already be stored on the FLV slice. Any modification of default filters
        // should force a page refresh, so default filters will be constant throughout
        // a page session.
        if (!this.props.shouldDisableDefaultFilters && !this.props.isRegisteredFlv) {
            const defaultFilterKey = this.props.quorumDataType
                ? DjangIO.app.models.QuorumDataType.by_value(this.props.quorumDataType).key
                : undefined

            this.props.storeProcessedDefaultFilters(this.props.uniqueFlvId, defaultFilterKey)
        }
    }

    processPermanentFilters() {
        // woo permanent filter logic! This is applied before the FLV gets
        // rendered so that the first ListView load has the permanent filters applied.
        if (this.props.permanentFilters) {
            this.props.registerPermanentFilters(this.props.uniqueFlvId, this.props.permanentFilters)
        }

        // for the permanent filters defined via profiles, seeing as profiles only specify
        // filter keys (because their id is implicit)
        // there is a mild possibility that this could override the previous permanent filters. But
        // hopefully it won't be in an expanding way, only a limiting way.
        if (this.props.permanentFilterKey && this.props.profileId) {
            this.props.registerPermanentFilters(this.props.uniqueFlvId, {
                [this.props.permanentFilterKey]: this.props.profileId,
            })
        }

        if (this.props.orderBy) {
            this.props.registerPermanentFilters(this.props.uniqueFlvId, { order_by: this.props.orderBy })
        }
    }

    render() {
        return (
            Boolean(this.props.isRegisteredFlv && this.props.hasRegisteredFilters) && (
                <PlatformSpecificFilterableListView
                    resource={this.props.flvResource}
                    reachedEndOfData={!this.props.hasMoreDataToLoad}
                    hasNoResults={this.props.currentData.size === 0}
                    {...this.props}
                />
            )
        )
    }
}

/*
 * WE HAVE TO WRAP THIS GUY TO MAKE SURE THE FLV DOESN'T LOAD BEFORE
 * THE CUSTOM FIELDS LOAD, OR ELSE THE CUSTOM FIELDS WILL NEVER REGISTER
 * IN TIME
 */
class FLVWrapper extends Component {
    componentDidMount() {
        if (!this.props.isProfile && this.props.setSearchBarData) {
            this.props.setSearchBarData(
                this.props.quorumDataType,
                this.props.searchPlaceholderText,
                this.props.uniqueFlvId,
            )
        }
    }

    render() {
        return this.props.searchPreloadDataLoaded ? (
            <FilterableListView {...this.props} />
        ) : (
            <LoadingSpinner id="search-page-loading" />
        )
    }
}

export default FLVWrapper
